import ImageProvider, { PanoramaView, ProvidedImage } from "./ImageProvider"
import { LicenseInfo } from "./LicenseInfo"
import Wikimedia from "../Web/Wikimedia"
import Wikimedia_commons_white from "../../assets/svg/Wikimedia_commons_white.svelte"
import { Feature, Point } from "geojson"
import { ServerSourceInfo } from "../../Models/SourceOverview"
import { ComponentType } from "svelte/types/runtime/internal/dev"

/**
 * This module provides endpoints for wikimedia and others
 */
export class WikimediaImageProvider extends ImageProvider {
    public static readonly singleton = new WikimediaImageProvider()
    public static readonly apiUrls = [
        "https://commons.wikimedia.org/wiki/",
        "https://upload.wikimedia.org",
    ]
    public static readonly commonsPrefixes = [
        ...WikimediaImageProvider.apiUrls,
        "http://commons.wikimedia.org/wiki/",
        "http://upload.wikimedia.org",
        "File:",
    ]
    private readonly commons_key = "wikimedia_commons"
    public readonly defaultKeyPrefixes = [this.commons_key, "image"]
    public readonly name = "Wikimedia"

    private constructor() {
        super()
    }

    /**
     * Replaces (multiple) spaces to underscores.
     * Will remove a "File:"-prefix
     *
     * WikimediaImageProvider.makeCanonical("Some File.jpg") // => "Some_File.jpg"
     *
     * // Double spaces
     * WikimediaImageProvider.makeCanonical("Some      File.jpg") // => "Some_File.jpg"
     * WikimediaImageProvider.makeCanonical("Some+File.jpg") // => "Some+File.jpg"
     *
     * // Remove File: prefix
     */
    private static makeCanonical(filename: string): string {
        if (filename.startsWith("File:")) {
            filename = filename.substring(5)
        }

        return filename.trim().replace(/\s+/g, "_")
    }

    /**
     *
     * WikimediaImageProvider.extractFileName("https://commons.wikimedia.org/wiki/File:Somefile.jpg") // => "Somefile.jpg"
     * WikimediaImageProvider.extractFileName("https://commons.wikimedia.org/wiki/File:S%C3%A8vres%20-%20square_madame_de_Pompadour_-_bo%C3%AEte_%C3%A0_livres.jpg?uselang=en") // => "Sèvres_-_square_madame_de_Pompadour_-_boîte_à_livres.jpg"
     * WikimediaImageProvider.extractFileName("https://upload.wikimedia.org/wikipedia/commons/thumb/e/e2/Edinburgh_City_police_box_001.jpg/576px-Edinburgh_City_police_box_001.jpg")// => "Edinburgh_City_police_box_001.jpg"
     */
    public static extractFileName(url: string) {
        if (!url.startsWith("http")) {
            return url
        }
        const thumbMatch = url.match(this.thumbUrlRegex)
        if (thumbMatch) {
            return thumbMatch[1]
        }
        const path = decodeURIComponent(new URL(url).pathname)
        return WikimediaImageProvider.makeCanonical(path.substring(path.lastIndexOf("/") + 1))
    }

    private static PrepareUrl(value: string, useHd = false): string {
        if (value.toLowerCase().startsWith("https://commons.wikimedia.org/wiki/")) {
            return value
        }
        const baseUrl = `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(
            value
        )}`
        if (useHd) {
            return baseUrl
        }
        return baseUrl + `?width=500&height=400`
    }

    private static startsWithCommonsPrefix(value: string): boolean {
        return WikimediaImageProvider.commonsPrefixes.some((prefix) => value.startsWith(prefix))
    }

    private static readonly thumbUrlRegex = /^https:\/\/upload.wikimedia.org\/.*\/([^/]+)\/.*-\1/
    private static removeCommonsPrefix(value: string): string {
        if (value.startsWith("https://upload.wikimedia.org/")) {
            value = value.substring(value.lastIndexOf("/") + 1)
            value = decodeURIComponent(value)
        }

        if (value.startsWith("File:")) {
            return value
        }
        for (const prefix of WikimediaImageProvider.commonsPrefixes) {
            if (value.startsWith(prefix)) {
                let part = value.substr(prefix.length)
                if (prefix.startsWith("http")) {
                    part = decodeURIComponent(part)
                }
                return part
            }
        }
        return value
    }

    apiUrls(): ServerSourceInfo[] {
        return WikimediaImageProvider.apiUrls.map(
            (url) =>
                <ServerSourceInfo>{
                    url,
                    description: "Wikimedia contains various images",
                    openData: true,
                    sourceAvailable: true,
                    category: "core",
                    selfhostable: true,
                }
        )
    }

    sourceIcon(): ComponentType {
        return Wikimedia_commons_white
    }

    public PrepUrl(value: NonNullable<string>): ProvidedImage
    public PrepUrl(value: undefined): undefined

    public PrepUrl(value: string): ProvidedImage
    public PrepUrl(value: string | undefined): ProvidedImage | undefined {
        if (value === undefined) {
            return undefined
        }
        value = WikimediaImageProvider.removeCommonsPrefix(value)

        if (value.startsWith("File:")) {
            return this.UrlForImage(value)
        }

        // We do a last effort and assume this is a file
        return this.UrlForImage("File:" + value)
    }

    /**
     *
     * @param key
     * @param value
     * @constructor
     *
     * const result = await WikimediaImageProvider.singleton.ExtractUrls("wikimedia_commons", "File:Sèvres_-_square_madame_de_Pompadour_-_boîte_à_livres.jpg")
     * result[0].url_hd // => "https://commons.wikimedia.org/wiki/Special:FilePath/File%3AS%C3%A8vres_-_square_madame_de_Pompadour_-_bo%C3%AEte_%C3%A0_livres.jpg"
     *
     */
    public async ExtractUrls(key: string, value: string): undefined | Promise<ProvidedImage[]> {
        const hasCommonsPrefix = WikimediaImageProvider.startsWithCommonsPrefix(value)
        if (key !== undefined && key !== this.commons_key && !hasCommonsPrefix) {
            return undefined
        }

        value = WikimediaImageProvider.removeCommonsPrefix(value)
        if (value.startsWith("Category:")) {
            const urls = await Wikimedia.getCategoryContents(value)
            return urls
                .filter((url) => url.startsWith("File:"))
                .map((image) => this.UrlForImage(image))
        }
        if (value.startsWith("File:")) {
            return [this.UrlForImage(value, key, value)]
        }
        if (value.startsWith("http")) {
            // Probably an error
            return undefined
        }
        // We do a last effort and assume this is a file
        return [this.UrlForImage("File:" + value, key, value)]
    }

    public async DownloadAttribution(img: { id: string }): Promise<LicenseInfo> {
        const l = await Wikimedia.getLicenseFor("https://commons.wikimedia.org", img.id)
        return <LicenseInfo>{
            title: img.id,
            artist: l.authors.join(", "),
            licenseShortName: l.license,
            informationLocation: { href: this.visitUrl(img) },
        }
    }

    private UrlForImage(image: string, key?: string, value?: string): ProvidedImage {
        image = "File:" + WikimediaImageProvider.makeCanonical(image)
        const providedImage: ProvidedImage = {
            url: WikimediaImageProvider.PrepareUrl(image),
            url_hd: WikimediaImageProvider.PrepareUrl(image, true),
            key: undefined,
            provider: this,
            id: image,
            isSpherical: false,
        }
        if (key && value) {
            providedImage.originalAttribute = { key, value }
        }
        return providedImage
    }

    getPanoramaInfo(): Promise<Feature<Point, PanoramaView>> | undefined {
        return undefined
    }

    visitUrl(img: { id: string }): string | undefined {
        return "https://commons.wikimedia.org/wiki/" + img.id
    }
}
